import Vue from 'vue';
import axios from 'axios';
import gql from 'graphql-tag';
const decode = require('unescape');

export default {
    namespaced: true,
    state: {
        prerenderMode: undefined,
        loading: false,
        pageModules: {},
        resource: null,
        page: null,
        currentTemplate: '#page-content',
        defaultTemplate: '#page-content',
        pagesCache: {},
        seoUrlCache: {},
        pagesScripts: {},
    },
    mutations: {
        update(state, payload) {
            if (payload.params.pageId || payload.params.resource) {
                let key;
                if (payload.params.pageId) {
                    key = payload.params.pageId;
                } else if (payload.params.resource) {
                    key = payload.params.resource;
                }

                if (!state.pageModules[key]) {
                    Vue.set(state.pageModules, key, {});
                }

                const modules = state.pageModules[key];
                const newModules = [];
                Object.values(payload.result).forEach(pageModule => {
                    if (!modules[pageModule.layout_area_name]) {
                        Vue.set(modules, pageModule.layout_area_name, {});
                    }
                    if (
                        !modules[pageModule.layout_area_name][pageModule.id] ||
                        modules[pageModule.layout_area_name][pageModule.id].language != pageModule.language
                    ) {
                        Vue.set(modules[pageModule.layout_area_name], pageModule.id, pageModule);
                    }
                    newModules.push(pageModule.id);
                });
                Object.keys(modules).forEach(areaName => {
                    const area = modules[areaName];
                    Object.values(area).forEach(pageModule => {
                        if (newModules.indexOf(pageModule.id) === -1) {
                            Vue.delete(modules[areaName], pageModule.id);
                        }
                    });
                });
            }
        },
        loading(state, loading) {
            state.loading = loading;
        },
        setResource(state, resource) {
            state.resource = resource;
        },
        setCurrentPage(state, page) {
            if (page && page.merchant_id && Vue.$search) {
                Vue.$search.setMerchant(page.merchant_id);
            }
            state.page = page;
        },
        setCurrentTemplate(state, template) {
            state.currentTemplate = template;
        },
        addPagesCache(state, options) {
            state.pagesCache[options.key] = options.result;
        },
        addSeoUrlCache(state, options) {
            state.seoUrlCache[options.key] = options.result;
        },
        addPagesScripts: (state, { name, scripts }) => (state.pagesScripts[name] = scripts),
        setPrerenderMode: (state, value) => (state.prerenderMode = value),
    },
    actions: {
        clearPageModules({ state }, options) {
            if (options.pageId && state.pageModules[options.pageId]) {
                if (options.layout_area) {
                    state.pageModules[options.pageId][options.layout_area] = null;
                } else {
                    state.pageModules[options.pageId] = null;
                }
            } else if (options.resource && state.pageModules[options.resource]) {
                if (options.layout_area) {
                    state.pageModules[options.resource][options.layout_area] = null;
                } else {
                    state.pageModules[options.resource] = null;
                }
            }
        },
        loadPageModules({ commit, state }, options) {
            let existingPageModules;
            if (options.pageId && state.pageModules[options.pageId]) {
                existingPageModules = state.pageModules[options.pageId];
            } else if (options.resource && state.pageModules[options.resource]) {
                existingPageModules = state.pageModules[options.resource];
            } else if (
                options.layout_area &&
                state.page &&
                state.pageModules[state.page.id] &&
                state.pageModules[state.page.id][options.layout_area]
            ) {
                existingPageModules = state.pageModules[state.page.id][options.layout_area];
            }

            if (
                !existingPageModules ||
                !options.layout_area ||
                !existingPageModules[options.layout_area] ||
                options.override
            ) {
                commit('loading', true);
                return Vue.$graphql
                    .query({
                        query: gql`
                            query pageModules(
                                $language: String!
                                $pageId: [String!]
                                $inheritancePagesIds: [Int!]
                                $layout_area: String
                                $resource: [String!]
                            ) {
                                pageModules(
                                    pageId: $pageId
                                    inheritancePagesIds: $inheritancePagesIds
                                    layout_area: $layout_area
                                    resource: $resource
                                ) {
                                    language: $language
                                    id
                                    layout_area_name
                                    content
                                    additional_tags
                                    ordering
                                    display_delay
                                    composite_page_module_id
                                    display_on_desktop
                                    display_on_mobile
                                    language
                                }
                            }
                        `,
                        variables: {
                            language: options.language || Vue.$language.getLanguage(),
                            pageId: options.pageId,
                            inheritancePagesIds: options.inheritancePagesIds,
                            layout_area: options.layout_area,
                            resource: options.resource,
                        },
                    })
                    .then(r => r.data)
                    .then(data => {
                        commit('update', {
                            params: Object.assign({}, options),
                            result: data.pageModules,
                        });
                        commit('loading', false);
                    })
                    .catch(() => {
                        commit('loading', false);
                    });
            }
        },
        loadPage({ state, commit }, options) {
            if (state.pagesCache[JSON.stringify(options)]) {
                return new Promise(resolve => {
                    resolve(state.pagesCache[JSON.stringify(options)]);
                });
            }

            return Vue.$graphql
                .query({
                    query: gql`
                        query page(
                            $pageUrl: String
                            $pageResource: String
                            $country: Int
                            $language: String
                            $id: Int
                        ) {
                            page(
                                pageUrl: $pageUrl
                                pageResource: $pageResource
                                country: $country
                                language: $language
                                id: $id
                            ) {
                                id
                                merchant_id
                                title
                                url_path_part
                                inherit_page_id
                                template_file
                                layout_file
                                resource
                                url
                                attributes
                                page_modules {
                                    id
                                    layout_area_name
                                    content
                                    additional_tags
                                    ordering
                                    display_delay
                                    composite_page_module_id
                                    display_on_desktop
                                    display_on_mobile
                                    display_on_prerendering
                                    language
                                }
                            }
                        }
                    `,
                    fetchPolicy: 'no-cache',
                    variables: {
                        id: parseInt(options.pageId),
                        pageResource: options.resource,
                        pageUrl: options.path,
                        language: options.language || Vue.$language.getLanguage(),
                        country: options.country,
                    },
                })
                .then(r => r.data)
                .then(data => {
                    let result = data && data.page ? data.page : {};

                    commit('addPagesCache', {
                        key: JSON.stringify(options),
                        result: result,
                    });

                    return result;
                });
        },
        getSeoUrl({ state, commit }, options) {
            if (state.seoUrlCache[JSON.stringify(options)]) {
                return new Promise(resolve => {
                    resolve(state.seoUrlCache[JSON.stringify(options)]);
                });
            }

            return Vue.$graphql
                .query({
                    query: gql`
                        query seoUrl($pageUrl: String!) {
                            seoUrl(pageUrl: $pageUrl) {
                                url
                                type
                                entity_id
                            }
                        }
                    `,
                    fetchPolicy: 'no-cache',
                    variables: {
                        pageUrl: options.path,
                    },
                })
                .then(r => r.data)
                .then(data => {
                    let result = data && data.seoUrl ? data.seoUrl : {};

                    commit('addSeoUrlCache', {
                        key: JSON.stringify(options),
                        result: result,
                    });

                    return result;
                })
                .catch(() => {
                    return false;
                });
        },
        switchTemplate({ commit, dispatch }, options) {
            if (options && typeof options.name === 'object') {
                commit('setCurrentTemplate', options.name);
            } else {
                return dispatch('getContentTemplate', options);
            }
        },
        getContentTemplate({ commit, state }, template) {
            if (state.currentTemplate != template) {
                if (template) {
                    let path = '/layouts/';
                    let templateName = template;
                    let prefix = '';

                    if (typeof template == 'object') {
                        path = template.path;
                        templateName = template.name;
                        if (typeof template.prefix === 'string' && template.prefix.length > 0) {
                            prefix = template.prefix;
                        }
                    }

                    let componentName = `${prefix}${templateName.replace('.', '-')}`;

                    const pageName = Vue.$language.getLanguage() + '_' + componentName;
                    if (!Vue.options.components[componentName] && !Vue.options.components[pageName]) {
                        let prefix = `${Vue.$language.getLanguage()}_`;
                        if (state.prerenderMode) {
                            prefix = `prerender_${prefix}`;
                        }
                        const requestEndpoint = `${path}${prefix}${templateName}`;
                        return axios
                            .get(requestEndpoint)
                            .then(r => r.data)
                            .then(results => {
                                const content = decode(results);
                                const pattern = /<script[\s\S]*?>[\s\S]*?<\/script>/gi;
                                if (!state.pagesScripts[pageName]) {
                                    const scripts = content.match(pattern);
                                    const preparedScripts = [];
                                    if (scripts) {
                                        for (const script of scripts) {
                                            const contentPattern = /<script(.*?)>(.*?)<\/script>/gi;
                                            const [, attributes, content] = [...script.matchAll(contentPattern)][0];
                                            const preparedScript = {
                                                text: content,
                                            };
                                            attributes.split(' ').forEach(attribute => {
                                                const [key, value] = attribute.split('=');
                                                if (key) {
                                                    preparedScript[key] = value;
                                                }
                                            });
                                            preparedScripts.push(preparedScript);
                                        }
                                    }
                                    commit('addPagesScripts', { name: pageName, scripts: preparedScripts });
                                }
                                Vue.component(pageName, {
                                    template: `<div>${content.replace(pattern, '')}</div>`,
                                });
                                commit('setCurrentTemplate', pageName);
                            })
                            .catch(() => {
                                commit('setCurrentTemplate', template.fallback ? template.fallback : null);
                            });
                    } else if (typeof Vue.options.components[componentName] === 'function') {
                        return Vue.$prepareComponentInstance(Vue.options.components[componentName]).then(component => {
                            Vue.options.components[componentName] = component;
                            commit('setCurrentTemplate', componentName);
                        });
                    } else if (Vue.options.components[componentName]) {
                        commit('setCurrentTemplate', componentName);
                    } else {
                        commit('setCurrentTemplate', pageName);
                    }
                } else {
                    commit('setCurrentTemplate', state.defaultTemplate);
                }
            }
        },
    },
};
